import React from 'react';
import { FileTextIcon, TruckIcon, CalendarIcon, BuildingIcon, MapPinIcon, DollarSignIcon, UserIcon } from 'lucide-react';
import { PurchaseOrder } from './ReceiptingView';
import { formatCurrency } from '../utils/currencyFormatter';
import { StatusBadge } from '../StatusBadge';
interface ReceiptHeaderProps {
  purchaseOrder: PurchaseOrder;
  locale?: string;
}
export function ReceiptHeader({
  purchaseOrder,
  locale = 'en-US'
}: ReceiptHeaderProps) {
  // Get status badge type based on PO status
  const getStatusBadgeType = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
      case 'confirmed':
        return 'success';
      case 'pending approval':
      case 'pending confirmation':
        return 'pending';
      case 'rejected':
      case 'cancelled':
        return 'error';
      default:
        return 'info';
    }
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-14317">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-14318">
        <div className="flex justify-between items-center" data-id="element-14319">
          <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-14320">
            <FileTextIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-14321" />
            Purchase Order Details
          </h3>
          <StatusBadge status={getStatusBadgeType(purchaseOrder.status)} text={purchaseOrder.status} size="md" data-id="element-14322" />
        </div>
      </div>
      <div className="px-4 py-5 sm:p-6" data-id="element-14323">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-14324">
          <div data-id="element-14325">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14326">
              Purchase Order Information
            </h4>
            <div className="space-y-3" data-id="element-14327">
              <div className="flex items-start" data-id="element-14328">
                <div className="flex-shrink-0" data-id="element-14329">
                  <FileTextIcon className="h-5 w-5 text-gray-400" data-id="element-14330" />
                </div>
                <div className="ml-3" data-id="element-14331">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14332">
                    {purchaseOrder.id}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-14333">{purchaseOrder.name}</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14334">
                <div className="flex-shrink-0" data-id="element-14335">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-14336" />
                </div>
                <div className="ml-3" data-id="element-14337">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14338">
                    Order Date
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-14339">{purchaseOrder.date}</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14340">
                <div className="flex-shrink-0" data-id="element-14341">
                  <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-14342" />
                </div>
                <div className="ml-3" data-id="element-14343">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14344">
                    Total Value
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-14345">
                    {formatCurrency(purchaseOrder.total, purchaseOrder.currency, locale)}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-14346">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14347">
              Vendor Information
            </h4>
            <div className="space-y-3" data-id="element-14348">
              <div className="flex items-start" data-id="element-14349">
                <div className="flex-shrink-0" data-id="element-14350">
                  <UserIcon className="h-5 w-5 text-gray-400" data-id="element-14351" />
                </div>
                <div className="ml-3" data-id="element-14352">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14353">Vendor</p>
                  <p className="text-sm text-gray-500" data-id="element-14354">
                    {purchaseOrder.vendor}
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14355">
                <div className="flex-shrink-0" data-id="element-14356">
                  <TruckIcon className="h-5 w-5 text-gray-400" data-id="element-14357" />
                </div>
                <div className="ml-3" data-id="element-14358">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14359">
                    Delivery Status
                  </p>
                  <div className="mt-1" data-id="element-14360">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-14361">
                      Partially Delivered
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-14362">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14363">
              Shipping Information
            </h4>
            <div className="space-y-3" data-id="element-14364">
              <div className="flex items-start" data-id="element-14365">
                <div className="flex-shrink-0" data-id="element-14366">
                  <BuildingIcon className="h-5 w-5 text-gray-400" data-id="element-14367" />
                </div>
                <div className="ml-3" data-id="element-14368">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14369">Entity</p>
                  <p className="text-sm text-gray-500" data-id="element-14370">
                    {purchaseOrder.entity}
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14371">
                <div className="flex-shrink-0" data-id="element-14372">
                  <MapPinIcon className="h-5 w-5 text-gray-400" data-id="element-14373" />
                </div>
                <div className="ml-3" data-id="element-14374">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14375">Site</p>
                  <p className="text-sm text-gray-500" data-id="element-14376">{purchaseOrder.site}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}